import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Backdrop,
  Box,
  Container,
  CircularProgress,
  Grid,
} from '../../../components/mui/Core/index'
import GenericNav from '../../../components/genericcomponents/navbars/GenericNav'
import { fetchInventoryItems } from './inventory-slice'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import { InventoryItemTable } from './components/InventoryItemTable/inventory-item-table'
import TableSearch from '../../../components/TableSearch/table-search'
import useBrowserTabTitles from '../../../hooks/browserTabTitles/browser-tab-titles'
import PageTitle from '../../../components/PageTitle/page-title'
import Constants from '../../../constants/en.json'
import SelectClientDropdown from '../../../components/dropdowns/SelectClientDropdown/select-client-dropdown'
import ClientSelection from '../../../components/ClientSelection/client-selection'
import { userPermissionConstants } from '../../../constants/user-permission-constants'
import { hasUserPermission } from '../../../utils/user-utils'
import { sessionStorageKeyConstants } from '../../../constants/session-storage-key-constants'
import LabeledCheckbox from '../../../components/LabeledCheckbox/labeled-checkbox'

function Inventory() {
  const dispatch = useDispatch()
  const isFetchingInventoryItems = useSelector((state) => state.inventory.isFetchingInventoryItems)
  const fetchInventoryItemsError = useSelector((state) => state.inventory.fetchInventoryItemsError)
  const searchByOptions = [
    { id: 'itemNumber', text: 'NDC/Item #', type: 'string' },
    { id: 'itemDescription', text: 'Item Description', type: 'string' },
  ]
  const [tableFilterValue, setTableFilterValue] = useState('')
  const [tableFilterBy, setTableFilterBy] = useState(searchByOptions[0].id)
  const [isIncludeZeroQtyItems, setIsIncludeZeroQtyItems] = useState(false)
  const [selectedClient, setSelectedClient] = useState(
    JSON.parse(sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT)),
  )
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const hasSwitchClientPermission = hasUserPermission({
    permissionCode: userPermissionConstants.SWITCH_CLIENT,
    permissions: authenticatedUser?.permissions,
  })

  const browserTabTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${Constants.inventory.INVENTORY}`
    : Constants.inventory.INVENTORY
  useBrowserTabTitles(browserTabTitle)

  useEffect(() => {
    if (!hasSwitchClientPermission || (hasSwitchClientPermission && selectedClient)) {
      dispatch(fetchInventoryItems())
    }
  }, [dispatch, hasSwitchClientPermission, selectedClient])

  useEffect(() => {
    if (fetchInventoryItemsError) {
      dispatch(
        setSnackbar({
          message: fetchInventoryItemsError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }
  }, [dispatch, fetchInventoryItemsError])

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    setTableFilterValue(inputValue)
    setTableFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }

  const handleSelectClientDropdown = (client) => {
    setSelectedClient(client)
    returnSearch('', 0) // Reset the table filter
    setIsIncludeZeroQtyItems(false)
  }

  const handleIncludeZeroQtyItems = () => {
    setIsIncludeZeroQtyItems((prev) => !prev)
  }

  const tableSearchWrapperStyle = {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <GenericNav selected='Inventory' user='authenticated' />
      <Backdrop open={isFetchingInventoryItems}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='xl'>
        <Grid container spacing={3}>
          <Grid size={{ xs: hasSwitchClientPermission ? 12 : 6 }}>
            <PageTitle title={Constants.inventory.INVENTORY} />
          </Grid>
          {hasSwitchClientPermission &&
            (!selectedClient ? (
              <ClientSelection
                handleSelectOption={handleSelectClientDropdown}
                message={Constants.inventory.CLIENT_SELECTION_MESSAGE}
              />
            ) : (
              <>
                <Grid
                  container
                  alignItems='center'
                  justifyContent='space-between'
                  spacing={2}
                  sx={{ width: '100%' }}
                >
                  <Grid>
                    <SelectClientDropdown
                      currentState={selectedClient}
                      handleSelectOption={handleSelectClientDropdown}
                    />
                  </Grid>

                  <Grid sx={tableSearchWrapperStyle}>
                    <Grid container spacing={2} justifyContent='flex-end'>
                      <Grid>
                        <LabeledCheckbox
                          label={Constants.inventory.INCLUDE_ZERO_QUANTITY_MESSAGE}
                          checked={isIncludeZeroQtyItems}
                          onChange={handleIncludeZeroQtyItems}
                        />
                      </Grid>
                      <Grid>
                        <TableSearch
                          key={selectedClient?.clientCode || 'clientCode'}
                          returnSearch={returnSearch}
                          searchByOptions={searchByOptions}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <InventoryItemTable
                    filterBy={tableFilterBy}
                    filterValue={tableFilterValue}
                    searchByOptions={searchByOptions}
                    isIncludeZeroQtyItems={isIncludeZeroQtyItems}
                  />
                </Grid>
              </>
            ))}
          {!hasSwitchClientPermission && (
            <>
              <Grid container alignItems='flex-end' size={{ xs: 6 }}>
                <Grid sx={tableSearchWrapperStyle}>
                  <Grid size={{ xs: 6 }}>
                    <LabeledCheckbox
                      label={Constants.inventory.INCLUDE_ZERO_QUANTITY_MESSAGE}
                      checked={isIncludeZeroQtyItems}
                      onChange={handleIncludeZeroQtyItems}
                    />
                  </Grid>
                  <Grid>
                    <TableSearch returnSearch={returnSearch} searchByOptions={searchByOptions} />
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <InventoryItemTable
                  filterBy={tableFilterBy}
                  filterValue={tableFilterValue}
                  searchByOptions={searchByOptions}
                  isIncludeZeroQtyItems={isIncludeZeroQtyItems}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Container>
      <GenericSnackbar />
    </Box>
  )
}

export default Inventory
